import { downloadBlob } from './openDocument';

type ExportHtmlToPdfOptions = {
  fileName: string;
  rootId: string;
  widthPx?: number;
  marginMm?: number;
  sectionSelector?: string;
};

const stripBody = (html: string) => html.replace(/^[\s\S]*<body[^>]*>/i, '').replace(/<\/body>[\s\S]*$/i, '');
const extractStyles = (html: string) => {
  const styles = html.match(/<style[^>]*>[\s\S]*?<\/style>/gi)?.join('') ?? '';
  const links = html.match(/<link\b[^>]*rel=["']?(?:stylesheet|preconnect)["']?[^>]*>/gi)?.join('') ?? '';
  return links + styles;
};
const DEFAULT_MARGIN_MM = 14;
const PAGE_BOTTOM_SAFETY_PX = 28;
const BREAK_SPACER_CLASS = '__pdf_page_break_spacer';
const DEFAULT_SAFE_SELECTOR = 'p, li, h1, h2, h3, h4, h5, h6, tr, .row, .panel, .signature, .footer, hr, [data-pdf-section], [data-pdf-keep]';

async function waitForRender(container: HTMLElement, doc: Document) {
  const imagePromises = Array.from(container.querySelectorAll('img')).map((img) => {
    if (img.complete) return Promise.resolve();
    return new Promise<void>((resolve) => {
      img.onload = () => resolve();
      img.onerror = () => resolve();
    });
  });
  // Force le chargement explicite des fontes utilisées dans les rendus
  // (Libre Baskerville + Source Sans 3). Sans ce préchargement, fonts.ready
  // peut résoudre avant que les @font-face Google ne soient réellement
  // téléchargés, ce qui provoque des chevauchements de glyphes dans le PDF.
  const fontLoads = doc.fonts
    ? [
        doc.fonts.load("400 12px 'Libre Baskerville'"),
        doc.fonts.load("700 12px 'Libre Baskerville'"),
        doc.fonts.load("400 11px 'Source Sans 3'"),
        doc.fonts.load("600 11px 'Source Sans 3'"),
        doc.fonts.load("700 11px 'Source Sans 3'"),
      ].map((p) => p.catch(() => undefined))
    : [];
  await Promise.all([
    doc.fonts?.ready ?? Promise.resolve(),
    ...fontLoads,
    ...imagePromises,
    new Promise((resolve) => setTimeout(resolve, 150)),
  ]);
}

function insertPageBreakSpacers(pageRoot: HTMLElement, pageHeightCssPx: number, selector: string, doc: Document = document) {
  pageRoot.querySelectorAll(`.${BREAK_SPACER_CLASS}`).forEach((el) => el.remove());

  const bottomGuardPx = PAGE_BOTTOM_SAFETY_PX;
  const maxAttempts = 80;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const rootTop = pageRoot.getBoundingClientRect().top;
    const elements = Array.from(pageRoot.querySelectorAll<HTMLElement>(selector))
      .filter((el) => !el.classList.contains(BREAK_SPACER_CLASS))
      // Ne jamais couper à l'intérieur d'un bloc `data-pdf-section` SAUF si
      // ce bloc est plus grand qu'une page (sinon son titre se retrouverait
      // orphelin en bas de la page précédente).
      .filter((el) => {
        if (el.hasAttribute('data-pdf-section')) return true;
        const section = el.closest('[data-pdf-section]') as HTMLElement | null;
        if (!section) return true;
        const sectionTooTall = section.getBoundingClientRect().height >= pageHeightCssPx - bottomGuardPx;
        return sectionTooTall;
      });


    let inserted = false;

    for (const el of elements) {
      const rect = el.getBoundingClientRect();
      if (rect.height <= 0 || rect.width <= 0) continue;

      const top = rect.top - rootTop;
      const bottom = rect.bottom - rootTop;
      const pageIndex = Math.floor(top / pageHeightCssPx);
      const pageTop = pageIndex * pageHeightCssPx;
      const pageBottom = pageTop + pageHeightCssPx;
      const topInPage = top - pageTop;
      const bottomInPage = bottom - pageTop;
      const isTooTallToMove = rect.height >= pageHeightCssPx - bottomGuardPx;
      const crossesBottomMargin = bottomInPage > pageHeightCssPx - bottomGuardPx;
      const startsInBottomMargin = topInPage > pageHeightCssPx - bottomGuardPx;
      const headingNearBottom = /^H[1-6]$/.test(el.tagName) && pageHeightCssPx - topInPage < 90;

      if ((startsInBottomMargin || crossesBottomMargin || headingNearBottom) && topInPage > 18 && !isTooTallToMove) {
        const spacer = doc.createElement('div');
        spacer.className = BREAK_SPACER_CLASS;
        spacer.setAttribute('aria-hidden', 'true');
        spacer.style.cssText = `height:${Math.max(1, pageBottom - top + 1)}px;break-before:page;page-break-before:always;`;
        el.parentElement?.insertBefore(spacer, el);
        inserted = true;
        break;
      }
    }

    if (!inserted) break;
  }
}

type Stage = { doc: Document; cleanup: () => void };

/** Préfixe les règles CSS pour les confiner au conteneur de rendu (mode repli). */
function scopeCss(css: string, rootSelector: string): string {
  return css.replace(/<\/?style[^>]*>/gi, '').replace(/(^|\})\s*([^@{}]+)\{/g, (_m, brace, selectors) => {
    const scoped = selectors
      .split(',')
      .map((sel: string) => {
        const s = sel.trim();
        if (!s) return s;
        if (/^(html|body|:root)\b/i.test(s)) return `${rootSelector}${s.replace(/^(html|body|:root)/i, '')}`;
        return `${rootSelector} ${s}`;
      })
      .join(',');
    return `${brace} ${scoped}{`;
  });
}

/**
 * Crée la surface de rendu hors écran.
 * On privilégie une iframe (isolation des styles) mais certains contextes
 * d'hébergement bloquent l'accès au document de l'iframe (erreur cross-origin) :
 * on bascule alors sur un conteneur masqué dans la page, avec CSS confiné.
 */
function createStage(html: string, rootId: string, widthPx: number): Stage {
  const body = `<div id="${rootId}">${stripBody(html)}</div>`;
  try {
    const iframe = document.createElement('iframe');
    iframe.style.cssText = `position:fixed;left:-10000px;top:0;width:${widthPx}px;height:100vh;border:none;`;
    document.body.appendChild(iframe);
    const idoc = iframe.contentDocument;
    if (!idoc) throw new Error('iframe inaccessible');
    idoc.open();
    idoc.write(`<!doctype html><html><head><meta charset="utf-8">${extractStyles(html)}</head><body>${body}</body></html>`);
    idoc.close();
    // Vérifie l'accès effectif (peut lever une erreur cross-origin).
    void idoc.body.childElementCount;
    return { doc: idoc, cleanup: () => iframe.remove() };
  } catch {
    const host = document.createElement('div');
    host.style.cssText = `position:fixed;left:-10000px;top:0;width:${widthPx}px;background:#fff;z-index:-1;`;
    const styleHtml = extractStyles(html);
    const links = styleHtml.match(/<link\b[^>]*>/gi)?.join('') ?? '';
    const rawCss = styleHtml.match(/<style[^>]*>([\s\S]*?)<\/style>/gi)?.join('\n') ?? '';
    const style = document.createElement('style');
    style.textContent = scopeCss(rawCss, `#${rootId}`);
    host.innerHTML = links + body;
    document.head.appendChild(style);
    document.body.appendChild(host);
    return {
      doc: document,
      cleanup: () => {
        host.remove();
        style.remove();
      },
    };
  }
}


/**
 * Stratégie : on garde la base rapide d'origine — un seul canvas par document.
 * Avant capture, on ajoute seulement des espaces de saut de page devant les
 * sections / paragraphes qui seraient coupés par la pagination A4.
 */
export async function exportHtmlToPdf(html: string, options: ExportHtmlToPdfOptions): Promise<void> {
  await exportHtmlsToPdf([html], options);
}

/** Génère le PDF et le renvoie sous forme de Blob (sans téléchargement). */
export async function renderHtmlsToPdfBlob(htmls: string[], options: ExportHtmlToPdfOptions): Promise<Blob> {
  return buildPdf(htmls, options);
}

export async function exportHtmlsToPdf(htmls: string[], options: ExportHtmlToPdfOptions): Promise<void> {
  const blob = await buildPdf(htmls, options);
  downloadBlob(blob, options.fileName);
}

async function buildPdf(htmls: string[], options: ExportHtmlToPdfOptions): Promise<Blob> {

  const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
    import('jspdf'),
    import('html2canvas'),
  ]);

  const widthPx = options.widthPx ?? 794;
  const pageWidthMm = 210;
  const pageHeightMm = 297;
  const marginMm = options.marginMm ?? DEFAULT_MARGIN_MM;
  const contentWidthMm = pageWidthMm - marginMm * 2;
  const contentHeightMm = pageHeightMm - marginMm * 2;
  const selector = options.sectionSelector ?? DEFAULT_SAFE_SELECTOR;

  const pdf = new jsPDF('p', 'mm', 'a4');
  let firstDoc = true;

  for (let docIndex = 0; docIndex < htmls.length; docIndex += 1) {
    const html = htmls[docIndex];
    const rootId = `${options.rootId}_${docIndex}`;

    const stage = createStage(html, rootId, widthPx);
    const idoc = stage.doc;

    try {
      await waitForRender(idoc.body, idoc);

      const root = idoc.getElementById(rootId) ?? idoc.body;

      const pageRoot = root.querySelector<HTMLElement>('.doc') ?? root;

      const scale = 1.5;
      const pxPerMm = (widthPx * scale) / contentWidthMm;
      const pageHeightCssPx = contentHeightMm * (widthPx / contentWidthMm);

      // Insère des sauts de page pour éviter qu'un bloc soit coupé.
      insertPageBreakSpacers(pageRoot, pageHeightCssPx, selector, idoc);

      // Une seule capture canvas pour tout le document (rapide).
      const canvas = await html2canvas(pageRoot, {
        scale,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        imageTimeout: 0,
        windowWidth: widthPx,
      });

      if (!firstDoc) pdf.addPage();
      firstDoc = false;

      const pageHeightPx = Math.floor(contentHeightMm * pxPerMm);
      const totalHeightPx = canvas.height;
      let cursor = 0;
      let firstSlice = true;

      while (cursor < totalHeightPx - 1) {
        const remaining = totalHeightPx - cursor;
        if (remaining <= 1) break;
        const sliceHeight = Math.min(pageHeightPx, remaining);
        const slice = document.createElement('canvas');
        slice.width = canvas.width;
        slice.height = sliceHeight;
        const ctx = slice.getContext('2d')!;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, slice.width, slice.height);
        ctx.drawImage(canvas, 0, cursor, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);
        const sliceMm = sliceHeight / pxPerMm;
        if (!firstSlice) pdf.addPage();
        firstSlice = false;
        pdf.addImage(slice.toDataURL('image/jpeg', 0.9), 'JPEG', marginMm, marginMm, contentWidthMm, sliceMm, undefined, 'FAST');
        cursor += sliceHeight;
      }
    } finally {
      stage.cleanup();
    }

  }


  return pdf.output('blob') as Blob;
}

